import { ChangeEvent, memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { DEVICE_TYPE } from '@/services/Constants';

import Spinner from '@/components/spinner/Spinner';

interface TextareaProps {
	label: string;
	classname?: string;
	color?: 'gray';
	dataName?: string;
	dataPrivate?: boolean;
	disabled?: boolean;
	disabledError?: boolean;
	error?: string | boolean;
	floatingLabel?: boolean;
	focus?: boolean;
	loading?: boolean;
	name?: string;
	onChange?: (value: string) => void;
	onFocus?: () => void;
	readonly?: boolean;
	rows?: number;
	value?: string;
}

const Textarea = ({
	classname = '',
	color,
	dataName = '',
	dataPrivate = true,
	disabled = false,
	disabledError = false,
	floatingLabel = false,
	focus,
	label,
	loading,
	name = '',
	onChange = () => {},
	onFocus = () => {},
	readonly = false,
	rows = 1,
	value,
	error,
}: TextareaProps) => {
	const inputRef = useRef<HTMLTextAreaElement>(null);

	const [fieldHeight, setFieldHeight] = useState<string>('auto');

	let activeState = false;

	useLayoutEffect(() => {
		if (inputRef.current) {
			setFieldHeight(`${inputRef.current.scrollHeight}px`);
		}

		return () => {
			DEVICE_TYPE.type === 'ios' &&
				activeState &&
				window.dispatchEvent(new Event('keyboardDidHide'));
		};
	}, []);

	useEffect(() => {
		if (inputRef.current) {
			setFieldHeight(`${inputRef.current.scrollHeight}px`);
		}
	}, [value]);

	useEffect(() => {
		if (focus && inputRef.current) {
			inputRef.current.focus();
		}
	}, [focus]);

	const handleOnchange = (e: ChangeEvent<HTMLTextAreaElement>) => {
		const { selectionStart, selectionEnd } = e.target;
		setFieldHeight('auto');
		onChange(e.target.value);
		requestAnimationFrame(() => {
			if (inputRef.current) {
				inputRef.current.selectionStart = selectionStart;
				inputRef.current.selectionEnd = selectionEnd;
			}
		});
	};

	const handleOnBlur = () => {
		activeState = false;
		DEVICE_TYPE.type === 'ios' && window.dispatchEvent(new Event('keyboardDidHide'));
	};

	const handleOnFocus = () => {
		activeState = true;
		DEVICE_TYPE.type === 'ios' && window.dispatchEvent(new Event('keyboardDidShow'));
		onFocus && onFocus();
	};

	return (
		<div className={classNames('form-field', classname)}>
			<div
				className={classNames('input-field textarea-field notranslate', {
					error,
					'hide-placeholder': floatingLabel,
					'hide-label': !floatingLabel,
					'light-gray': color === 'gray',
				})}
			>
				<textarea
					ref={inputRef}
					className={classNames('notranslate', {
						error,
						'hide-label': !floatingLabel,
						'light-gray': color === 'gray',
					})}
					onFocus={handleOnFocus}
					onBlur={handleOnBlur}
					onChange={handleOnchange}
					placeholder={floatingLabel ? '*' : label}
					disabled={disabled}
					data-name={dataName || name}
					rows={rows}
					style={{ height: fieldHeight }}
					value={value}
					readOnly={readonly}
					data-private={dataPrivate}
				/>

				{floatingLabel && <label>{label}</label>}

				{loading && <Spinner />}
			</div>
			{!disabledError && (
				<span className="error-msg" data-name={name}>
					{error}
				</span>
			)}
		</div>
	);
};

export default memo(Textarea);
